// src/hooks/useAuth.ts
import { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENV_OK } from '../lib/supabase/browserClient';
import { DISABLE_AUTH } from '../config/flags';

export type UserProfile = {
  id: string;
  email?: string | null;
  plan?: string | null;
  trial_ends_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

// ---------- DEV / NO-AUTH MODE ----------
const DEV_USER: Partial<User> = {
  id: 'dev-user-id',
  email: 'dev@mystara.app',
} as any;

const DEV_PROFILE: UserProfile = {
  id: 'dev-user-id',
  email: 'dev@mystara.app',
  plan: 'cosmic',
  trial_ends_at: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export function useAuth() {
  // 1) Explicit dev mode – short-circuit everything
  if (DISABLE_AUTH) {
    const signIn  = async () => ({ user: DEV_USER } as any);
    const signUp  = async () => ({ user: DEV_USER } as any);
    const signOut = async () => {};
    return {
      user: DEV_USER as User,
      profile: DEV_PROFILE,
      loading: false,
      isConfigured: true,
      signIn,
      signUp,
      signOut,
    };
  }

  // 2) Supabase not configured – don’t block the app
  if (!SUPABASE_ENV_OK || !supabase) {
    const noop = async () => { throw new Error('Auth not configured'); };
    return {
      user: null as User | null,
      profile: null as UserProfile | null,
      loading: false,
      isConfigured: false,
      signIn: noop,
      signUp: noop,
      signOut: async () => {},
    };
  }

  // 3) Normal Supabase mode
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isConfigured] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolveProfile = (u: User | null): UserProfile | null => {
      if (!u) return null;
      // Replace with real DB fetch later if needed
      return {
        id: u.id,
        email: u.email ?? null,
        plan: 'trial',
        trial_ends_at: null,
        created_at: null,
        updated_at: null,
      };
    };

    // Timeout guard: never hang the UI
    const guard = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 3000);

    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) console.warn('[Mystara] getSession error:', error);
        const currentUser = data?.session?.user ?? null;
        if (!cancelled) {
          setUser(currentUser);
          setProfile(resolveProfile(currentUser));
        }
      } finally {
        if (!cancelled) setLoading(false);
        clearTimeout(guard);
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const u = session?.user ?? null;
        setUser(u);
        setProfile(resolveProfile(u));
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(guard);
      subscription?.subscription?.unsubscribe?.();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  return { user, profile, loading, isConfigured, signIn, signUp, signOut };
}
